import { StateCreator, create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { SELECTED_SIMS_KEY } from '@constants/storageKeys';
import { Sim } from '@generated/gql/graphql';

interface SelectedSimsState {
  sims: Sim[];
  addSim: (sim: Sim) => void;
  clearSims: () => void;
  loadSims: (sims: Sim[]) => void;
  removeSim: (simId: string) => void;
  replaceSim: (prevSimId: string, sim: Sim) => void;
}

/**
 * This store holds the user's SIM selection only. Discounts are server-derived
 * and change on every requote, so they are stripped on the way in and joined
 * back on when read, via `withQualifiedDiscounts`. Keeping them out means the
 * store — which is persisted to sessionStorage — can never go stale.
 */
const withoutDiscounts = (sim: Sim): Sim => ({
  ...sim,
  selectedTariff: { ...sim.selectedTariff, discounts: [] },
});

const createSelectedSimsSlice: StateCreator<SelectedSimsState> = (set) => ({
  sims: [],
  addSim: (sim) =>
    set((state) => ({
      ...state,
      sims: [...state.sims, withoutDiscounts(sim)],
    })),
  clearSims: () =>
    set((state) => ({
      ...state,
      sims: [],
    })),
  loadSims: (sims) =>
    set((state) => ({
      ...state,
      sims: sims.map(withoutDiscounts),
    })),
  removeSim: (simId) =>
    set((state) => ({
      ...state,
      sims: state.sims.filter((sim) => sim.id !== simId),
    })),
  // Editing swaps one SIM for another. This has to be a single update:
  // `useSyncSims` publishes every store change to a synced appointment, so
  // removing and then adding would broadcast an intermediate selection that
  // the editing client never renders itself. A passive client applying that
  // selection loses the SIM its `edited-sim-quote-id` still points at.
  replaceSim: (prevSimId, sim) =>
    set((state) => ({
      ...state,
      sims: [
        ...state.sims.filter(({ id }) => id !== prevSimId),
        withoutDiscounts(sim),
      ],
    })),
});

const useSimsStore = create<SelectedSimsState>()(
  persist(createSelectedSimsSlice, {
    name: SELECTED_SIMS_KEY,
    storage: createJSONStorage(() => sessionStorage),
  })
);

export const subscribeToSims = (callback: (sims: Sim[]) => void) => {
  let prevState = useSimsStore.getState();

  return useSimsStore.subscribe((newState) => {
    const prev = prevState.sims;
    const state = newState.sims;

    if (JSON.stringify(prev) !== JSON.stringify(state)) {
      callback(state);
    }

    prevState = newState;
  });
};

export { useSimsStore };
